import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import usePermission from "../../config/permissions";
import { BASE_URL } from "../../config/axios";
import { confirmToast, CustomTooltip, handleErrorToast } from "../CustomFunctions";
import {
    Calender,
    DownArrowIcon,
    InfoIcon,
    RefreshIcon,
    SuccessIcon,
    HourGlass,
    LightBulbIcon,
    Office,
    Employee,
    ChartIcon,
    AddwithBlueCircle,
    HolidayIcon,
} from "../AllSvg";
import {
    badgeStyle,
    cardIconStyle,
    getBtnStyle,
    getResultCardStyle,
    spinnerStyle,
} from "../../style/CustomFunctionForCss";
import Select from "react-select";
import { CSSForDropdownFilter2, MONTH_OPTIONS } from "../CustomUtilities";


const today = new Date();
const currentMonth = (today.getMonth() + 1).toString();
const currentYear = today.getFullYear().toString();

const RunFixedAssetsAttendance = () => {
    const { SUB_COMPANIES, SUPER_ADMIN } = usePermission();
    const [showNote, setShowNote] = useState(true);

    // Sub‑company selection
    const [selectedSubCompany, setSelectedSubCompany] = useState(
        sessionStorage.getItem("company_id") || ""
    );

    // Month & Year
    const [month, setMonth] = useState(currentMonth);
    const [year, setYear] = useState(currentYear);

    // UI states
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // Helper for month label
    const selectedMonthLabel =
        MONTH_OPTIONS.find((m) => m.value === month)?.label || "";

    // ─────────────────────────────────────────────────────────────────────────
    //  Execute API call
    // ─────────────────────────────────────────────────────────────────────────
    const runFixedAssetsAttendance = async () => {
        // Validation: sub‑company must be selected
        const companyId =
            SUPER_ADMIN && selectedSubCompany === "null"
                ? null
                : selectedSubCompany;

        if (!companyId) {
            toast.error("Please select a sub-company before running the process.");
            return;
        }

        const confirmMessage = `Run Fixed Assets Attendance for ${selectedMonthLabel} ${year} under the selected company?`;

        confirmToast(confirmMessage, async () => {
            setLoading(true);
            setResult(null);

            const payload = {
                month: parseInt(month, 10),
                year: parseInt(year, 10),
                sub_company: companyId,
            };

            try {

                const response = await axios.post(
                    `${BASE_URL}/wfm/mark-fixed-asset-attendance-with-log/`,
                    payload
                );

                if (response.data?.status === true) {
                    toast.success(response.data.message || "Fixed assets attendance processed successfully!");
                    setResult(response.data);
                } else {
                    throw new Error(response.data?.message || "Unexpected response format");
                }
            } catch (err) {
                handleErrorToast(err);
                setResult(null);
            } finally {
                setLoading(false);
            }
        });
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  Render summary cards when result is available
    // ─────────────────────────────────────────────────────────────────────────
    const renderSummary = () => {
        if (!result?.summary) return null;

        const {
            employees_processed,
            attendance_created,
            attendance_updated,
            present_count,
            holiday_count,
            weeklyoff_count,
        } = result.summary;

        // Helper for individual metric card
        const MetricCard = ({ label, value, icon, color, tooltipText }) => (
            <CustomTooltip
                tooltipContent={
                    <div
                        style={{
                            background: "#2d3748",
                            color: "#fff",
                            fontSize: "12px",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            maxWidth: "220px",
                            textAlign: "center",
                            lineHeight: "1.4",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                    >
                        {tooltipText}
                    </div>
                }
            >
                <div
                    style={{
                        background: "#f8f9fa",
                        borderRadius: "8px",
                        padding: "12px 10px",
                        textAlign: "center",
                        flex: "1",
                        minWidth: "200px",
                        maxWidth: "210px",
                        border: `1px solid ${color}20`,
                        cursor: "help",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                    }}
                >
                    <div style={{ fontSize: "20px", color }}>{icon}</div>
                    <div style={{ fontSize: "22px", fontWeight: 700, color: "#1e293b" }}>
                        {value ?? 0}
                    </div>
                    <div style={{ fontSize: "11px", color: "#5f6c80" }}>{label}</div>
                </div>

            </CustomTooltip>

        );

        return (
            <div style={{ marginTop: "20px" }}>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "12px",
                        justifyContent: "space-between",
                    }}
                >
                    <MetricCard
                        label="Employees Processed"
                        value={employees_processed}
                        icon={<Employee />}
                        color="#3b82f6"
                        tooltipText="Number of permanent employees (fixed assets) processed."
                    />
                    {/* <MetricCard
                        label="Attendance Created"
                        value={attendance_created}
                        icon={<AddwithBlueCircle />}
                        color="#10b981"
                        tooltipText="No of days (in selected month)"
                    /> */}
                    <MetricCard
                        label="Attendance Updated"
                        value={attendance_updated}
                        icon={<RefreshIcon size={14} color="rgb(21, 101, 192)" />}
                        color="#f59e0b"
                        tooltipText={`Total attendance updates = (Employees Processed × No of Days in month).`}
                    />
                    <MetricCard
                        label="Present"
                        value={present_count}
                        icon={<SuccessIcon size={25} bgColor="#cdebcf" tickColor="#2e7d32" />}
                        color="#22c55e"
                        tooltipText={`Total present days across all employees = Working Days x Employee processed.`}

                    />
                    <MetricCard
                        label="Holidays"
                        value={holiday_count}
                        icon={<HolidayIcon />}
                        color="#ef4444"
                        tooltipText="Number of public holidays in the selected month."
                    />
                    <MetricCard
                        label="Weekly Off"
                        value={weeklyoff_count}
                        icon={<Calender />}
                        color="#8b5cf6"
                        tooltipText="Number of weekly off days x Employee processed in the selected month."
                    />
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    //  Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <div style={{ margin: "10px" }} className="content-tabs">
                {/* ── Page header ── */}
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        marginBottom: "18px",
                        marginTop: "25px",
                    }}
                >
                    <span style={{ marginTop: "-3px" }}>
                        <RefreshIcon size={14} color="rgb(21, 101, 192)" />
                    </span>
                    <div>
                        <h4
                            className="att-syn-rlm-heading mb-0"
                            style={{ fontSize: "17px" }}
                        >
                            Run Fixed Assets Attendance
                        </h4>
                        <span className="font-12" style={{ color: "#78909c" }}>
                            Process attendance records linked to fixed assets for a specific
                            company
                        </span>
                    </div>
                </div>

                {/* ── Info note (collapsible) ──
                <div
                    style={{
                        background: "#fff8e1",
                        border: "1px solid #ffe082",
                        borderLeft: "5px solid #ffa000",
                        borderRadius: "6px",
                        padding: "14px 18px",
                        marginBottom: "20px",
                        fontSize: "13px",
                        color: "#5d4037",
                        lineHeight: "1.75",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            userSelect: "none",
                        }}
                        onClick={() => setShowNote((p) => !p)}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span>
                                <InfoIcon />
                            </span>
                            <strong style={{ fontSize: "14px", color: "#e65100" }}>
                                Note — Fixed Assets Attendance Processing
                            </strong>
                        </div>
                        <span style={{ fontSize: "16px", color: "#ffa000", fontWeight: 700 }}>
                            {showNote ? "▲" : "▼"}
                        </span>
                    </div>

                    {showNote && (
                        <div style={{ marginTop: "12px" }}>
                            <ul style={{ margin: "0 0 12px 22px", padding: 0, lineHeight: "1.9" }}>
                                <li>
                                    <strong>Purpose:</strong> This process links biometric / attendance
                                    data with fixed assets (e.g., laptops, equipment) assigned to
                                    employees. It ensures accurate tracking of asset usage.
                                </li>
                                <li>
                                    <strong>Scope:</strong> Runs for the selected <strong>sub‑company</strong>,
                                    month, and year. Only the chosen sub‑company will be affected.
                                </li>
                                <li>
                                    <strong>After execution:</strong> The summary shows how many employees
                                    were processed, how many attendance records were created/updated,
                                    and counts for present, holiday, and weekly‑off days.
                                </li>
                            </ul>
                            <div
                                style={{
                                    background: "#fff3e0",
                                    border: "1px solid #ffb74d",
                                    borderRadius: "4px",
                                    padding: "9px 12px",
                                }}
                            >
                                <strong>⚠️ Important:</strong> Only run this after the daily
                                attendance sync is complete for the selected month. The process
                                may take a few minutes depending on the number of employees.
                            </div>
                        </div>
                    )}
                </div> */}

                {/* ── Main execution card ── */}
                <div className="att-syn-rlm-card">
                    <div
                        className="table-heading-flex"
                        style={{ marginBottom: "14px" }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={cardIconStyle("#e3f2fd", "#1565c0")}>
                                <ChartIcon />
                            </span>
                            <div>
                                <h5 className="att-syn-rlm-heading mb-0">
                                    Fixed Assets Attendance
                                </h5>
                                <span className="att-syn-rlm-subheading">
                                    Process attendance for fixed asset employees
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ gap: "16px" }} className="navbar-subsection">
                        {/* Month picker */}
                        <div className="field-cont-div">
                            <Calender />
                            <input
                                type="month"
                                className="attendance-input-field calender-input width-10vw"
                                value={`${year}-${month.padStart(2, "0")}`}
                                onChange={(e) => {
                                    const [y, m] = e.target.value.split("-");
                                    setYear(y);
                                    setMonth(m.replace(/^0+/, ""));
                                }}
                            />
                            <hr className="field-cont-hr" />
                        </div>

                        {/* Sub‑company selector (only for super admin) */}
                        {SUPER_ADMIN && (
                            <>
                                <div className="field-cont-div">
                                    <Office />
                                    <select
                                        className="attendance-input-field width-10vw date-field"
                                        value={selectedSubCompany}
                                        onChange={(e) => setSelectedSubCompany(e.target.value)}
                                    >
                                        <option value="null">Select Sub Company</option>
                                        {SUB_COMPANIES.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.title}
                                            </option>
                                        ))}
                                    </select>
                                    <hr className="field-cont-hr" />
                                </div>
                            </>
                        )}

                        {/* Execute button */}
                        <button
                            onClick={runFixedAssetsAttendance}
                            disabled={loading}
                            className="upload-svg"
                            style={getBtnStyle({
                                bg: "#1565c0",
                                disabled: loading,
                                alignSelf: "flex-end",
                            })}
                        >
                            {loading ? (
                                <>
                                    <span style={spinnerStyle} />
                                    Processing...
                                </>
                            ) : (
                                "Run Fixed Assets Attendance"
                            )}
                        </button>
                    </div>

                    {/* Loading state with progress bar */}
                    {loading && (
                        <div
                            style={getResultCardStyle({
                                background: "#e3f2fd",
                                borderColor: "#90caf9",
                                leftBorderColor: "#1565c0",
                                borderRadius: "6px",
                                padding: "14px 16px",
                                marginTop: "16px",
                                textColor: "#1b5e20",
                                fontSize: "13px",
                            })}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    marginBottom: "10px",
                                }}
                            >
                                <span className="spinner-loader" />
                                <div>
                                    <strong style={{ fontSize: "13px", color: "#0d47a1" }}>
                                        Processing fixed assets attendance for {selectedMonthLabel} {year}
                                        {SUPER_ADMIN && selectedSubCompany !== "null"
                                            ? ` | Sub-company: ${SUB_COMPANIES.find((c) => c.id === selectedSubCompany)
                                                ?.title || selectedSubCompany
                                            }`
                                            : ""}
                                    </strong>
                                    <br />
                                    <span style={{ fontSize: "12px", color: "#1565c0" }}>
                                        <HourGlass size={15} color="#f7a831" /> This may take a few
                                        minutes. Do not close or refresh the page.
                                    </span>
                                </div>
                            </div>
                            <div
                                style={{
                                    height: "6px",
                                    borderRadius: "4px",
                                    background: "#bbdefb",
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        height: "100%",
                                        width: "40%",
                                        background: "#1565c0",
                                        borderRadius: "4px",
                                        animation: "progressSlide 1.4s ease-in-out infinite",
                                    }}
                                />
                            </div>
                            <p
                                style={{
                                    margin: "8px 0 0",
                                    fontSize: "11px",
                                    color: "#5c8fc7",
                                }}
                            >
                                <LightBulbIcon size={15} color="#faf623" /> Tip: The summary
                                will appear here once processing is complete.
                            </p>
                        </div>
                    )}

                    {/* Success result with summary */}
                    {result && !loading && result.status === true && (
                        <div
                            style={getResultCardStyle({
                                background: "#e8f5e9",
                                borderColor: "#a5d6a7",
                                leftBorderColor: "#2e7d32",
                                borderRadius: "6px",
                                padding: "12px 16px",
                                marginTop: "14px",
                                textColor: "#1b5e20",
                                fontSize: "13px",
                            })}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <SuccessIcon size={25} bgColor="#cdebcf" tickColor="#2e7d32" />
                                <strong>Fixed Assets Attendance Processed Successfully</strong>
                            </div>
                            <span className="color-card-cobalt-green font-12" style={{ display: "block", marginTop: "8px" }}>
                                {result.message}
                            </span>
                            {renderSummary()}
                        </div>
                    )}
                </div>
            </div>

            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};

export default RunFixedAssetsAttendance;